import React, { useState, useRef } from 'react';
import { CameraIcon, XCircleIcon } from '@heroicons/react/24/outline';

/**
 * ProfilePictureUploader component for uploading and previewing profile pictures
 * @param {Object} props - Component props
 * @param {Function} props.onImageSelect - Function called when an image is selected
 * @param {string} props.error - Error message to display
 * @param {boolean} props.touched - Whether the input has been touched
 * @param {string} props.defaultImage - Default image URL to display (optional)
 */
const ProfilePictureUploader = ({ onImageSelect, error, touched, defaultImage }) => {
  const [previewUrl, setPreviewUrl] = useState(defaultImage || null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleFileSelect = (file) => {
    if (!file.type.startsWith('image/')) {
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);

    // Pass the file to parent component
    onImageSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files.length) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleClearImage = () => {
    setPreviewUrl(null);
    onImageSelect(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700 mb-1">Photo de profil</label>
      <div
        className={`relative flex items-center justify-center border-2 border-dashed rounded-full h-32 w-32 mx-auto cursor-pointer overflow-hidden transition-all ${
          isDragging ? 'border-primary bg-primary/10' : 'border-gray-300 hover:border-primary'
        } ${error && touched ? 'border-red-500' : ''}`}
        onClick={() => fileInputRef.current && fileInputRef.current.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {previewUrl ? (
          <div className="relative h-full w-full">
            <img 
              src={previewUrl} 
              alt="Preview" 
              className="h-full w-full object-cover" 
            />
            <button
              type="button"
              className="absolute top-0 right-0 bg-white/75 rounded-full p-1 m-1"
              onClick={(e) => {
                e.stopPropagation();
                handleClearImage();
              }}
            >
              <XCircleIcon className="h-5 w-5 text-gray-700" />
            </button>
          </div>
        ) : (
          <div className="text-center p-4">
            <CameraIcon className="h-10 w-10 text-gray-400 mx-auto" />
            <p className="text-xs text-gray-500 mt-1">Cliquez ou glissez</p>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageChange}
        />
      </div>
      {error && touched && (
        <p className="text-red-500 text-xs mt-1">{error}</p>
      )}
    </div>
  );
};

export default ProfilePictureUploader; 